import { Injectable } from '@angular/core';
import { Offer } from '../../models/offer';
import { Router } from '@angular/router';
import { AuthService } from '../auth/auth.service';
import { GungModalService } from '../gung-modal/gung-modal.service';
import { TranslateService } from '@ngx-translate/core';
import { OfferService } from '../offers/offer.service';
import { SelectedCustomerService } from '../selected-customer/selected-customer.service';
import { CustomerService } from '../customers/customer.service';
import { OfferToBasketService } from '../offer-to-basket/offer-to-basket.service';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { BackendRerouteService } from '../utils/backend-reroute.service';
import { filter, first, forkJoin, Observable, of, switchMap } from 'rxjs';
import { ActionButton, ActionButtonConfigService } from 'gung-common';
import { Customer } from '../../models';
import { OrderChangeCustomerConfirmationModalComponent } from '../../components/order-change-customer-confirmation-modal/order-change-customer-confirmation-modal.component';

@Injectable({
  providedIn: 'root'
})
export class OfferActionConfigService implements ActionButtonConfigService<Offer> {
  base = [
    { title: 'CONVERT_TO_ORDER', action: (item: Offer) => this.convertToOrder(item) },
    { title: 'COPY_TO_CART', action: (item: Offer) => this.addOfferToCart(item) },
    {
      title: 'PDF',
      action: (item: Offer) => this.backendRerouteService.reroute(`download/offer/${item.id}`, true)
    },
    {
      title: 'DELETE_OFFER',
      action: (item: Offer) => this.deleteOffer(item)
    }
  ];

  extra = [
    {
      title: 'SHOW_ORDER',
      action: (item: Offer) => {
        this.router.navigate(['/offers/', item.id]);
      }
    }
  ];

  modalRef: NgbModalRef;

  constructor(
    protected router: Router,
    protected authService: AuthService,
    protected gungModalService: GungModalService,
    protected translationService: TranslateService,
    protected offerService: OfferService,
    protected selectedCustomerService: SelectedCustomerService,
    protected customerService: CustomerService,
    protected offerToBasketService: OfferToBasketService,
    protected modalService: NgbModal,
    protected backendRerouteService: BackendRerouteService
  ) {}

  getButtons(item: Offer): Observable<ActionButton<Offer>[]> {
    let base = this.base;
    return this.authService.getRoles().pipe(
      filter(roles => !!roles),
      first(),
      switchMap(roles => {
        if (!!this.router.url && this.router.url.indexOf('/offers/') < 0) {
          if (roles.indexOf('SALES') > -1) {
            return new Observable<ActionButton<Offer>[]>(subscriber => {
              subscriber.next([...base, ...this.extra]);
              subscriber.complete();
            });
          } else {
            return new Observable<ActionButton<Offer>[]>(subscriber => {
              subscriber.next([...this.extra]);
              subscriber.complete();
            });
          }
        } else {
          if (roles.indexOf('SALES') > -1) {
            return new Observable<ActionButton<Offer>[]>(subscriber => {
              subscriber.next(base);
              subscriber.complete();
            });
          } else {
            return new Observable<ActionButton<Offer>[]>(subscriber => {
              subscriber.next([]);
              subscriber.complete();
            });
          }
        }
      })
    );
  }

  convertToOrder(offer: Offer) {
    this.gungModalService
      .openConfirmYesNoModal(
        this.translationService.instant('CREATE_ORDER'),
        this.translationService.instant('CREATE_ORDER_FROM_OFFER') + ' ' + offer.id + '?'
      )
      .then(result => {
        if (result) {
          this.offerService.convertOfferToOrder(offer.id).subscribe({
            next: v => {
              this.gungModalService.openConfirmYesNoModal(
                this.translationService.instant('PENDING_ORDER_HEADER'),
                this.translationService.instant('SUCCESSFULLY_CREATED') +
                  ' ' +
                  this.translationService.instant('ORDER_ID') +
                  ': ' +
                  v,
                null,
                this.translationService.instant('SHOW_ORDER'),
                null
              );
              this.router.navigate(['/orders/', v]);
            },
            error: e => {
              console.error(e);
              this.gungModalService.openConfirmYesNoModal(
                this.translationService.instant('Error'),
                e.error.message,
                null,
                this.translationService.instant('OK'),
                null
              );
            }
          });
        }
      });
  }

  deleteOffer(offer: Offer) {
    this.gungModalService
      .openConfirmYesNoModal(
        this.translationService.instant('DELETE_OFFER'),
        this.translationService.instant('DELETE_OFFER') + ' ' + offer.id + '?'
      )
      .then(result => {
        if (result) {
          this.offerService.deleteOffer(offer.id).subscribe({
            next: v => {
              window.location.reload();
            },
            error: e => {
              console.error(e);
              this.gungModalService.openConfirmYesNoModal(
                this.translationService.instant('Error'),
                e.error.message,
                null,
                this.translationService.instant('OK'),
                null
              );
            }
          });
        }
      });
  }

  addOfferToCart(item: Offer) {
    this.selectedCustomerService
      .getSelectedCustomer()
      .pipe(
        first(),
        switchMap(selectedCustomer => {
          return forkJoin({
            selectedCustomer: of(selectedCustomer),
            orderCustomer: this.customerService.getCustomer(item.deliveryCustomerId).pipe(first())
          });
        })
      )
      .subscribe(({ selectedCustomer, orderCustomer }) => {
        if (selectedCustomer.id === orderCustomer.id) {
          this.copyToCart(item);
        } else {
          this.changeCustomerDialog(selectedCustomer, orderCustomer, item);
        }
      });
  }
  protected copyToCart(item: Offer) {
    this.offerToBasketService.addOfferToBasket(item.id).then(
      result => {},
      reason => {}
    );
  }

  protected changeCustomerDialog(currentCustomer: Customer, orderCustomer: Customer, item: Offer) {
    this.modalRef = this.modalService.open(OrderChangeCustomerConfirmationModalComponent);
    this.modalRef.componentInstance.currentCustomer = currentCustomer;
    this.modalRef.componentInstance.nextCustomer = orderCustomer;

    this.modalRef.result
      .then(
        resolve => {
          if (resolve === 'Change') {
            this.selectedCustomerService.selectCustomer(orderCustomer.id);
          }
        },
        () => {
          throw new Error('Rejected');
        }
      )
      .then(() => this.copyToCart(item))
      .catch(error => console.error(error));
  }
}
